import React, { useEffect, useRef, useState } from "react";
import { Content, Row, Col, Inputs, Button } from "adminlte-2-react";
import { ButtonToolbar } from "react-bootstrap";
import moment from "moment";
import { API } from "../../constant";
import { useUnitRealtime } from "../../hooks/useUnitRealtime";
import { useUnitCommands } from "../../hooks/useUnitCommands";
import DeviationLog from "../../components/Modal/DeviationLog";
import UdgkLog from "../../components/Modal/UdgkLog";
import UdgLog from "../../components/Modal/UdgLog";
import ModalDialog from "../../components/Modal/ModalDialog";
import UdgkForm from "../../components/Modal/UdgkForm";
import UdgForm from "../../components/Modal/UdgForm";
import { hasPermission } from "../../helpers";
import { useAuthContext } from "../../context/AuthContext";
import { useNavigate } from "react-router-dom";
import { fetchWithAuth } from "./../../api/fetchWithAuth";

import "./charts/chartSetup";
import MainLineChart from "./charts/MainLineChart";
import Rt3MinChart from "./charts/Rt3MinChart";
import GouChart from "./charts/GouChart";

const { Date: Datepicker } = Inputs;

function Main(props) {
	const { permission, isLoading, setUser, logout } = useAuthContext();
	const navigate = useNavigate();

	const id = props.match.params.id;

	const measurementIdsRef = useRef({
		temperature: null,
		production: null,
	});

	// Данные графиков больше не проходят через React state — три
	// презентационных компонента ниже управляются императивно через refs
	// (см. их собственные комментарии: на каждый realtime-тик пересоздавать
	// весь датасет через React было бы заметно дороже точечного chart.js
	// update()). В state остаётся только то, что реально определяет
	// внешний вид страницы вокруг графиков.
	const [state, setState] = useState({
		title: "",
		temperatureValue: "н/д",
		pid: props.match.params.id,
		responseDate: moment().format("YYYY-MM-DD HH:mm:ss"),
		requestDate: moment().format("YYYY-MM-DD"),
		date: moment().format("YYYY-MM-DD"),
	});

	const commands = useUnitCommands();

	const lineChartRef = useRef(null);
	const rt3minChartRef = useRef(null);
	const gouChartRef = useRef(null);
	const grdata = useRef({});

	const convertCentrifugeMessage = (messageData) => {
		// Extract values from message.data using measurement IDs
		const ids = measurementIdsRef.current;

		const tempValue =
			ids.temperature != null ? messageData.data?.[ids.temperature] : null;

		const prodValue =
			ids.production != null ? messageData.data?.[ids.production] : null;

		const converted = {
			dtms: messageData.dtms || new Date().toISOString(),
			dts: messageData.dts || new Date().toISOString(),
			fact: {
				temp: tempValue,
				prod: prodValue,
				dv: messageData.deviation,
			},
			pbr: messageData.pbr || 0,
			udg: messageData.udg || 0,
			reserve: messageData.reserve || 0,
			min: messageData.min || 0,
			max: messageData.max || 0,
			plan: messageData.plan || {},
			dt: messageData.dt,
			"3min_blk_sec": messageData["3min_blk_sec"],
			"3min_blk_num": messageData["3min_blk_num"],
			udg_3min_min: messageData.udg_3min_min,
			udg_3min_max: messageData.udg_3min_max,
		};

		return converted;
	};

	useEffect(() => {
		if (id !== state.pid) {
			setState((prevState) => ({
				...prevState,
				pid: id,
			}));
		}
	}, [id]);

	useEffect(() => {
		if (!isLoading && !permission) {
			logout();
			setUser(undefined);
			navigate("/signin");
		}
	}, [permission, isLoading]);

	const handleMessage = (ctx) => {
		if (typeof ctx.data?.reload_client !== "undefined")
			window.location.reload();

		const ids = measurementIdsRef.current;

		if (ids.temperature == null || ids.production == null) {
			return;
		}

		try {
			// Convert new message format to old format
			const convertedData = convertCentrifugeMessage(ctx.data);

			// Check if we have valid production data
			if (!convertedData.fact.prod && convertedData.fact.prod !== 0) {
				console.warn("No production data available in message");
				return;
			}

			charts_update_rt_data(convertedData);
			updateTemperature(convertedData);
			charts_update_plan_data(convertedData);
		} catch (error) {
			console.error("Error processing Centrifuge message:", error);
		}
	};

	const { subscription } = useUnitRealtime(id, handleMessage);

	// Раньше это было частью эффекта, создающего подписку — теперь подписку
	// создаёт хук, а этот эффект просто реагирует на её появление/смену.
	useEffect(() => {
		if (!subscription || !id) return;

		fetchUnitsWithConfig(id);
		fetchData(id, state.date, subscription);
	}, [subscription, id]);

	const charts_update_plan_data = (ctx) => {
		if (typeof ctx.data?.plan === "undefined") return;
		lineChartRef.current?.setPlan(ctx.plan);
	};

	const charts_update_rt_data = (ctx) => {
		if (typeof ctx.fact?.prod === "undefined") return;

		grdata.current.rt_prev = grdata.current.rt;
		grdata.current.rt = ctx;

		const dt_prev = grdata.current?.rt_prev?.dt;
		const dt = grdata.current?.rt?.dt;

		if (typeof dt_prev !== "undefined" && dt_prev !== dt) {
			setState((prevState) => ({
				...prevState,
				date: dt,
			}));
			fetchData(state.pid, dt, subscription);
			return;
		}

		const station = grdata.current.station;
		lineChartRef.current?.applyRealtimeUpdate(ctx, station);
		rt3minChartRef.current?.applyRealtimeUpdate(ctx, station);
		gouChartRef.current?.applyRealtimeUpdate(ctx, station, "y");
	};

	const updateTemperature = (ctx) => {
		if (typeof ctx.fact?.temp === "undefined") return;

		setState((prevState) => ({
			...prevState,
			temperatureValue: ctx.fact.temp || "н/д",
		}));
	};

	const fetchData = async (pid, date, newSubscription) => {
		try {
			newSubscription.unsubscribe();
			const dateStr = moment(date).format("YYYY-MM-DD");
			const response = await fetchWithAuth(
				`${API}/enery-stats-khps?pid=${pid}&date=${dateStr}`,
			);
			const responseData = await response.json();
			drawStats(responseData, newSubscription);
		} catch (error) {
			console.error("Error fetching data:", error);
		}
	};

	const fetchUnitsWithConfig = async (pid) => {
		try {
			// Fetch unit with populated relationships
			const response = await fetchWithAuth(
				`${API}/units/${pid}?populate=temperatureTm,pbrTm`,
			);

			const responseData = await response.json();
			const unit = responseData.data;
			// Extract measurement IDs
			const tempId = unit.attributes.temperatureTm?.data?.id || null;
			const prodId = unit.attributes.pbrTm?.data?.id || null;

			const ids = {
				temperature: tempId,
				production: prodId,
			};

			measurementIdsRef.current = ids;

			setState((prevState) => ({
				...prevState,
				title: `Блок_${unit.attributes.name}`,
			}));

			console.log("Measurement IDs loaded:", {
				temperature: tempId,
				production: prodId,
			});
		} catch (error) {
			console.error("Error fetching units with config:", error);
		}
	};

	const drawStats = (data, newSubscription) => {
		const station = (grdata.current.station = data.station);
		const bucket = data.current_stats;

		energyView(bucket);

		if (station.id > 0) {
			setState((prevState) => ({
				...prevState,
				responseDate: data.response_date,
				requestDate: data.request_date,
			}));
		}

		if (data.current_date === data.request_date) {
			const xMin = new Date();
			xMin.setHours(0, 0, 0, 0);

			const xMax = new Date();
			xMax.setHours(new Date().getHours(), 0, 0, 0);

			lineChartRef.current?.setPbrAnnotation({
				type: "box",
				backgroundColor: "#8a8a8a11",
				xMin: moment(xMin).format("YYYY-MM-DD HH:mm:ss"),
				xMax: moment(xMax).format("YYYY-MM-DD HH:mm:ss"),
			});
			newSubscription.subscribe();
		} else {
			newSubscription.unsubscribe();
			lineChartRef.current?.setPbrAnnotation(null);
		}
	};

	// Локальная разовая трансформация данных под три графика — не React state
	// и не useMemo: результат сразу и целиком уходит в императивные
	// chart.setStats(...) ниже, между рендерами нигде не переиспользуется.
	const energyView = (bucket) => {
		const udgk = bucket.udgk;
		const udg = bucket.udg;
		const pbrs = bucket.pbr;
		const facts = [];
		const rt3mins = [];
		const rt3minsLabels = [];

		const gou_mode = "y";

		let maxv = -1000000000,
			minv = 1000000000;

		for (const k in bucket.pbr) {
			facts.push({
				x: k,
				y: bucket.rt[k],
			});

			if (bucket.pbr[k] < minv) minv = bucket.pbr[k];
			if (bucket.pbr[k] > maxv) maxv = bucket.pbr[k];
			if (bucket.udgk[k] < minv) minv = bucket.udgk[k];
			if (bucket.udgk[k] > maxv) maxv = bucket.udgk[k];
			if (bucket.udg[k] < minv) minv = bucket.udg[k];
			if (bucket.udg[k] > maxv) maxv = bucket.udg[k];
		}

		let max3min = -1000000000,
			min3min = 1000000000;

		let idx = 0;

		for (const k in bucket.rt3min) {
			rt3mins[idx] = bucket.rt3min[k];
			const minl = k.substring(6, 8);
			const mminl = k.substring(7, 8);
			const hminl = k.substring(0, 5);
			rt3minsLabels[idx] = minl === "00" ? hminl : mminl === "0" ? minl : "";

			if (bucket.rt3min[k] < min3min) min3min = bucket.rt3min[k];
			if (bucket.rt3min[k] > max3min) max3min = bucket.rt3min[k];

			idx++;
		}

		const mid3min = Math.round((max3min + min3min) / 2);

		rt3minChartRef.current?.setStats({ rt3mins, rt3minsLabels, mid3min });
		gouChartRef.current?.setStats({ minv, maxv, mode: gou_mode });
		lineChartRef.current?.setStats({ udgk, udg, facts, pbrs, minv, maxv });
	};

	return (
		<Content homeRoute="/" title={state.title} subTitle="">
			<ModalDialog
				title={`${state.title} :: Часовое отклонение за ${state.date}`}
				show={commands.modals.deviationLog}
				onHide={() => commands.closeModal("deviationLog")}
			>
				<DeviationLog unitId={state.pid} period={state.date} />
			</ModalDialog>
			<ModalDialog
				title={`${state.title} :: Лог комманд УДГК за ${state.date}`}
				show={commands.modals.udgkLog}
				onHide={() => commands.closeModal("udgkLog")}
			>
				<UdgkLog unitId={state.pid} period={state.date} />
			</ModalDialog>
			<ModalDialog
				modalSize="lg"
				title={`Регистрация команд УДГ: ${state.title}`}
				show={commands.modals.udgForm}
				onHide={commands.closeUdgForm}
				backdrop={true}
				modalFooter={
					commands.udgFormSubmitted ? (
						<div>
							<Button
								className="center-block"
								text="Закрыть окно"
								type="info"
								onClick={commands.closeUdgForm}
							/>
						</div>
					) : (
						<div>
							<Button
								text="Отменить"
								onClick={commands.closeUdgForm}
								pullLeft
							/>
							<Button
								text="Отправить"
								onClick={commands.toggleConfirmDialog}
								type="info"
								pullRight
							/>
						</div>
					)
				}
			>
				<UdgForm
					unitId={state.pid}
					onHideConfirmDialog={commands.toggleConfirmDialog}
					showResult={commands.markUdgFormSubmitted}
					showConfirmDialog={commands.confirmDialogOpen}
					udgUnitTitle="Регистрация команд УДГ : Краснодарская ТЭЦ"
				/>
			</ModalDialog>
			<ModalDialog
				modalSize="lg"
				title={`Относительная и абсолютная корректировка УДГК: ${state.title} `}
				backdrop={true}
				show={commands.modals.udgkForm}
				onHide={() => commands.closeModal("udgkForm")}
			>
				<UdgkForm unitId={state.pid} />
			</ModalDialog>
			<ModalDialog
				title={`${state.title} :: Лог комманд УДГ за ${state.date}`}
				show={commands.modals.udgLog}
				onHide={() => commands.closeModal("udgLog")}
			>
				<UdgLog unitId={state.pid} period={state.date} />
			</ModalDialog>

			<Row>
				<Col xs={12}>
					<div className="box">
						<div className="box-header">
							<h2 className="page-header">
								{"Выгрузка сформирована " +
									state.responseDate +
									" по дате " +
									state.requestDate}
							</h2>
							<Row>
								<Col md={12}>
									<div className="form-horizontal">
										<Col md={2}>
											<Datepicker
												label="Дата"
												className="datepicker"
												labelPosition="left"
												iconLeft="fas-calendar"
												format="YYYY-MM-DD"
												inputIconPosition="before"
												dateProps={{
													isOutsideRange: () => false,
													numberOfMonths: 1,
												}}
												value={state.date}
												onChange={(event) => {
													const {
														target: { value: value },
													} = event;
													setState((prevState) => ({
														...prevState,
														date: value,
													}));
												}}
											/>
										</Col>
										<Col md={2}>
											<Button
												type="success"
												icon="fas-play"
												text="Показать данные"
												onClick={() =>
													fetchData(state.pid, state.date, subscription)
												}
											/>
										</Col>
										<Col md={8}>
											<ButtonToolbar className="pull-right">
												<div className="btn bg-purple">
													{" "}
													<span> {state.temperatureValue}</span> {" °C"}{" "}
												</div>
												{!isLoading &&
													hasPermission(
														permission,
														"api::deviation-log",
														"deviation-log",
														"getLog",
													) && (
														<div className="btn-group">
															<Button type="danger" text="Отклонения">
																<a
																	onClick={() =>
																		commands.openModal("deviationLog")
																	}
																	href="#"
																>
																	Почасовые
																</a>
															</Button>
														</div>
													)}
												{!isLoading &&
													hasPermission(
														permission,
														"api::udgk-form",
														"udgk-form",
														"insertCommand",
													) && (
														<div className="btn-group">
															<Button type="primary" text="Регистрация команд">
																<a
																	onClick={() =>
																		commands.openModal("udgForm")
																	}
																	href="#"
																>
																	УДГ
																</a>
																<a
																	onClick={() =>
																		commands.openModal("udgkForm")
																	}
																	href="#"
																>
																	УДГК
																</a>
															</Button>
														</div>
													)}
												{!isLoading &&
													hasPermission(
														permission,
														"api::udg-log",
														"udg-log",
														"getLog",
													) && (
														<div className="btn-group">
															<Button type="info" text="Лог команд">
																<a
																	onClick={() =>
																		commands.openModal("udgLog")
																	}
																	href="#"
																>
																	УДГ
																</a>
																<a
																	onClick={() =>
																		commands.openModal("udgkLog")
																	}
																	href="#"
																>
																	УДГК
																</a>
															</Button>
														</div>
													)}
											</ButtonToolbar>
										</Col>
									</div>
								</Col>
								<Col md={8}></Col>
							</Row>
							<div className="chart box with-border">
								<MainLineChart ref={lineChartRef} />
								<Row>
									<Col md={8}>
										<Rt3MinChart ref={rt3minChartRef} />
									</Col>
									<Col md={4}>
										<GouChart ref={gouChartRef} />
									</Col>
								</Row>
							</div>
						</div>
					</div>
				</Col>
			</Row>
		</Content>
	);
}

export default Main;
